import type { SelectProps } from '@chakra-ui/react'
import { FormControl, Select } from '@chakra-ui/react'
import type { BaseAddressDto } from 'BrokerTsLib/src/dtos'
import type { FieldError } from 'BrokerTsLib/src/rhf'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'

import { FormGroup } from '../components/FormGroup'
import { FormLabel } from '../components/FormLabel'
import type { InputGroupProps } from '../components/InputGroup'
import { InputGroup } from '../components/InputGroup'
import { useCountries, useRegions } from '../hooks'
import { useNameof } from '../hooks/useNameof'


export const InsureeAddress = ({ addressPrefix, street = false, isDisabled, isDisabledStreet }: (InputGroupProps | SelectProps) & { addressPrefix: string, street?: boolean, isDisabledStreet?: boolean }) => {
  const banameof = useNameof<BaseAddressDto>(addressPrefix)
  const pcPath = banameof(x => x.postCode)
  const cityPath = banameof(x => x.city)
  const streetPath = banameof(x => x.street)
  const streetNumberPath = banameof(x => x.streetNumber)

  const {
    register,
    getFieldState,
    setValue,
    watch
  } = useFormContext()

  const postCodeField = watch(pcPath)
  const [validPsc, setValidPsc] = useState(false)

  const pcError = getFieldState(pcPath).error
  const cityError = getFieldState(cityPath).error
  const streetError = getFieldState(streetPath).error
  const streetNumberError = getFieldState(streetNumberPath).error

  useEffect(() => {
    setValidPsc(postCodeField.length == 5 && !pcError)
  }, [postCodeField, pcError])

  const { data: countriesData } = useCountries()
  const { data: citiesData } = useRegions(postCodeField, { enabled: validPsc })
  const cities = validPsc ? citiesData?.filter(x => (x.city?.length ?? 0) > 0) ?? [] : []

  return (
    <>
      {
        street &&
        <FormGroup>
          <FormControl isInvalid={!!streetError} isRequired>
            <FormLabel>Ulice</FormLabel>

            <InputGroup placeholder="Např. Dlouhá" htmlSize={24}
              {...register(streetPath)}
              isDisabled={isDisabled && isDisabledStreet}
              errors={streetError}
            />
          </FormControl>

          <FormControl isInvalid={!!streetNumberError} isRequired>
            <FormLabel>Číslo popisné nebo orientační</FormLabel>

            <InputGroup placeholder="Např. 123" htmlSize={4} {...register(streetNumberPath)} isDisabled={isDisabled && isDisabledStreet} errors={streetNumberError} />
          </FormControl>
        </FormGroup>
      }

      <FormGroup>
        <FormControl isInvalid={!!pcError} isRequired>
          <FormLabel htmlFor={pcPath} tooltip="Zadejte poštovní směrovací číslo obce. Údaj lze nalézt na webových stránkách www.psc.cz.">
            PSČ
          </FormLabel>
          <InputGroup
            id={pcPath}
            type='number'
            placeholder="00000"
            autoComplete="postal-code"
            errors={pcError as FieldError}
            htmlSize={4}
            isDisabled={isDisabled}
            {...register(pcPath, {
              onChange: () => setValue(cityPath, undefined, { shouldValidate: true })
            })}
          />
        </FormControl>

        <FormControl isRequired isInvalid={!!cityError}>
          <FormLabel>Město</FormLabel>

          {(cities?.length ?? 0) > 0 &&
            <Select isDisabled={!validPsc || cities.length === 0 || isDisabled} {...register(banameof(x => x.city))}>
              <option disabled value=''>
                Vyberte obec
              </option>
              {
                cities.map(item =>
                  <option value={item.city} key={item.city}>{item.city}</option>
                )
              }
            </Select>}
          {(cities?.length ?? 0) == 0 && <Select isDisabled={true} defaultValue=''><option disabled value=''>Vyberte obec</option></Select>}
        </FormControl>

        <FormControl>
          <FormLabel>Stát</FormLabel>

          <Select defaultValue="CZ" isDisabled={true} {...register(banameof(x => x.country))}>
            {
              countriesData?.map(item =>
                <option value={item.id} key={item.id}>{item.name}</option>
              )
            }
          </Select>
        </FormControl>
      </FormGroup>
    </>
  )
}